import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { uploadApi, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Upload, FileText, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

export function UploadPage() {
  const navigate = useNavigate();
  const [images, setImages] = useState<File[]>([]);
  const [annotationsFiles, setAnnotationsFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [duplicates, setDuplicates] = useState<string[]>([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [mergeDecisions, setMergeDecisions] = useState<Record<string, 'merge' | 'skip'>>({});
  const [storageWarning, setStorageWarning] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const annotationsInputRef = useRef<HTMLInputElement>(null);
  /** After Confirm, keep polling even if the job is still awaiting_confirmation. */
  const awaitingUserConfirmRef = useRef(true);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const checkStorageReadiness = useCallback(async (): Promise<{ ok: boolean; message?: string }> => {
    try {
      const { data } = await uploadApi.readiness();
      if (!data.ready) {
        const message = data.warnings[0] || data.storage.detail || 'Storage is not ready for uploads.';
        setStorageWarning(message);
        return { ok: false, message };
      }
      setStorageWarning(null);
      return { ok: true };
    } catch (err) {
      const message = getErrorMessage(err);
      setStorageWarning(message);
      return { ok: false, message };
    }
  }, []);

  useEffect(() => {
    void checkStorageReadiness();
  }, [checkStorageReadiness]);

  const pollStatus = useCallback(async (id: string) => {
    stopPolling();

    const check = async () => {
      try {
        const { data } = await uploadApi.status(id);
        setStatus(data.status);
        setProgress(data.total_files ? Math.round((data.processed_files / data.total_files) * 100) : 0);
        if (data.status === 'awaiting_confirmation') {
          if (!awaitingUserConfirmRef.current) {
            return 'continue';
          }
          stopPolling();
          setDuplicates(data.duplicate_samples);
          const decisions: Record<string, 'merge' | 'skip'> = {};
          data.duplicate_samples.forEach((s) => { decisions[s] = 'merge'; });
          setMergeDecisions(decisions);
          setShowDuplicateModal(true);
          setUploading(false);
          return 'stop';
        }
        if (data.status === 'completed') {
          stopPolling();
          setUploading(false);
          setErrorDetail(null);
          toast.success(`Upload complete: ${data.samples_created} samples, ${data.patches_added} patches`);
          navigate('/admin');
          return 'stop';
        }
        if (data.status === 'failed') {
          stopPolling();
          setUploading(false);
          const message = data.error_message || 'Upload failed';
          setErrorDetail(message);
          toast.error(message);
          return 'stop';
        }
      } catch (err) {
        stopPolling();
        setUploading(false);
        const message = getErrorMessage(err);
        setErrorDetail(message);
        toast.error(message);
        return 'stop';
      }
      return 'continue';
    };

    if (await check() === 'stop') return;
    pollIntervalRef.current = setInterval(async () => {
      if (await check() === 'stop') stopPolling();
    }, 1000);
  }, [navigate, stopPolling]);

  const handleUpload = async () => {
    if (!images.length || !annotationsFiles.length) {
      toast.error('Select images and at least one annotations file (XML or NPY)');
      return;
    }
    setErrorDetail(null);
    setUploading(true);
    awaitingUserConfirmRef.current = true;
    try {
      // Re-check storage right before upload so admins get a clear pre-flight alert.
      const readiness = await checkStorageReadiness();
      if (!readiness.ok) {
        const message = readiness.message || 'Storage is not ready for uploads.';
        setErrorDetail(message);
        setUploading(false);
        toast.error(message);
        return;
      }

      const { data } = await uploadApi.create(images, annotationsFiles);
      setJobId(data.job_id);
      setStatus('pending');
      void pollStatus(data.job_id);
    } catch (err) {
      setUploading(false);
      const message = getErrorMessage(err);
      setErrorDetail(message);
      toast.error(message);
    }
  };

  const confirmDuplicates = async () => {
    if (!jobId) return;
    awaitingUserConfirmRef.current = false;
    setUploading(true);
    setShowDuplicateModal(false);
    setErrorDetail(null);
    setStatus('processing');
    try {
      await uploadApi.confirm(jobId, mergeDecisions);
      void pollStatus(jobId);
    } catch (err) {
      awaitingUserConfirmRef.current = true;
      setShowDuplicateModal(true);
      setUploading(false);
      const message = getErrorMessage(err);
      setErrorDetail(message);
      toast.error(message);
    }
  };

  const storageBlocked = Boolean(storageWarning);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Upload Batch" subtitle="Images + XML or NPY annotations" backTo="/admin" />
      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {storageWarning && (
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertTitle>Storage not ready</AlertTitle>
            <AlertDescription>
              <p>{storageWarning}</p>
              <p className="mt-1 text-xs">
                Uploads are blocked until DevOps frees disk space or restores MinIO. Check server logs for details.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => void checkStorageReadiness()}
              >
                Recheck storage
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {errorDetail && !storageWarning && (
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertTitle>Upload error</AlertTitle>
            <AlertDescription>
              <p>{errorDetail}</p>
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Upload annotations</CardTitle>
            <CardDescription>
              Upload patch images with matching Cellpose segmentation files
              (one <code className="text-xs">*_seg.npy</code> per image, e.g. BH-08D.jpg + BH-08D_seg.npy;
              names like CIN3-301A / Cy-16A group into sample CIN3-301 / Cy-16),
              CVAT 1.1 XML, or JSON exported from this app.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div
              role="button"
              tabIndex={0}
              onClick={() => imageInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  imageInputRef.current?.click();
                }
              }}
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
            >
              <Upload className="size-10 mx-auto text-gray-400 mb-3" />
              <p className="text-sm text-gray-600 mb-1">Select all patch images</p>
              <p className="text-xs text-gray-500">Click anywhere here to browse</p>
              <input
                ref={imageInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => setImages(Array.from(e.target.files || []))}
                className="hidden"
              />
              {images.length > 0 && (
                <p className="text-xs text-green-600 mt-3">{images.length} images selected</p>
              )}
            </div>

            <div
              role="button"
              tabIndex={0}
              onClick={() => annotationsInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  annotationsInputRef.current?.click();
                }
              }}
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
            >
              <FileText className="size-8 mx-auto text-blue-600 mb-3" />
              <p className="text-sm font-medium text-gray-700 mb-1">Annotations (XML, NPY, or JSON)</p>
              <p className="text-xs text-gray-500 mb-3">
                Cellpose: one *_seg.npy per image · CVAT XML · or exported JSON
              </p>
              <input
                ref={annotationsInputRef}
                type="file"
                multiple
                accept=".xml,.npy,.json,application/xml,application/json"
                onChange={(e) => setAnnotationsFiles(Array.from(e.target.files || []))}
                className="hidden"
              />
              {annotationsFiles.length > 0 && (
                <p className="text-xs text-green-600">
                  {annotationsFiles.length} file{annotationsFiles.length === 1 ? '' : 's'} selected
                  </p>
                  )}
            </div>

            {uploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>
                    {progress === 0 && status === 'pending'
                      ? 'Uploading files to server…'
                      : `Processing… (${status})`}
                  </span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} />
                {progress === 0 && status === 'pending' && (
                  <p className="text-xs text-gray-500">
                    Large batches (100+ images) can take a few minutes to upload before processing starts.
                  </p>
                )}
              </div>
            )}

            <Button onClick={handleUpload} disabled={uploading || storageBlocked} className="w-full">
              {uploading ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Upload className="size-4 mr-2" />}
              Upload batch
            </Button>
          </CardContent>
        </Card>
      </main>

      <Dialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate samples detected</DialogTitle>
            <DialogDescription>
              Some samples already exist. Choose whether to merge new patches or skip.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {duplicates.map((name) => (
              <div key={name} className="flex items-center justify-between border rounded p-3">
                <span className="font-medium">{name}</span>
                <select
                  className="border rounded px-2 py-1 text-sm"
                  value={mergeDecisions[name] || 'merge'}
                  onChange={(e) =>
                    setMergeDecisions((d) => ({ ...d, [name]: e.target.value as 'merge' | 'skip' }))
                  }
                >
                  <option value="merge">Merge patches</option>
                  <option value="skip">Skip</option>
                </select>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={confirmDuplicates}>Confirm &amp; continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
